// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xGoogleAnalyticsState } from "@guestsupply/App/Store/Reducers/xGoogleAnalytics";
import { getDataLayer } from "@guestsupply/App/Utilities/DataLayer";
import { GOOGLE_EVENTS } from "@guestsupply/XDL/constants";
import { parseProductCategories } from "@guestsupply/XDL/Utilities";
import { Handler, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { deleteSession } from "@insite/client-framework/Services/SessionService";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    chain as CancelPunchOutCheckoutChain,
    DeleteSession,
} from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";

type HandlerType = Handler;

// xng-change: add GTM event
export const XngDeleteSession: HandlerType = props => {
    const state = props.getState() as xGoogleAnalyticsState;

    if (state.xGoogleAnalytics.includeCancelPunchOutCheckout) {
        const dataLayer = getDataLayer();
        const session = getSession(state);
        const locationId = session?.pickUpWarehouse?.id;
        const cart = getCurrentCartState(state).value;
        const currentBillTo = getCurrentBillToState(state).value;

        if (!dataLayer) {
            return;
        }

        dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.

        const googleAnalyticsData = {
            event: GOOGLE_EVENTS.CANCEL_PUNCHOUT_CART,
            ecommerce: {
                transaction_id: cart?.orderNumber,
                value: cart?.orderSubTotal,
                tax: cart?.totalTax,
                shipping: cart?.shippingAndHandling,
                currency: state.context.session.currency?.currencyCode,
                items: cart?.cartLines?.map(cartLine => ({
                    item_id: cartLine.erpNumber,
                    item_name: cartLine.shortDescription,
                    affiliation: currentBillTo?.companyName,
                    index: cartLine?.line,
                    item_brand: cartLine.brand?.name,
                    location_id: locationId,
                    price: cartLine.pricing?.unitNetPrice,
                    quantity: cartLine.qtyOrdered,
                    ...parseProductCategories(cartLine.properties?.categories),
                })),
                user: state.context.session.email || state.context.session.userName,
                brand_code: currentBillTo?.properties?.brand,
                exclusive: currentBillTo?.properties?.exclusive,
                group_descr: currentBillTo?.properties?.groupDescription,
                sales_rep: cart?.salespersonName,
            },
        };

        dataLayer.push(googleAnalyticsData);
    }

    return deleteSession();
};

replaceInChain(CancelPunchOutCheckoutChain, DeleteSession, XngDeleteSession);
